'use client';

import React, { useState } from 'react';
import { Box, Typography, Button, Dialog, Input } from 'src/components';
import { createAccountFolder } from 'src/api/accountFolders/accountFoldersApi';
import { useNotification } from 'src/context/notification';

interface CreateFolderDialogProps {
  isOpen: boolean;
  onClose: () => void;
  scope: 'PERSONAL' | 'COMPANY' | null;
  onCreateSuccess?: () => void;
}

const CreateFolderDialog: React.FC<CreateFolderDialogProps> = ({
  isOpen,
  onClose,
  scope,
  onCreateSuccess,
}) => {
  const [isCreating, setIsCreating] = useState(false);
  const [folderName, setFolderName] = useState('');
  const { setNotification } = useNotification();

  const handleCreate = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!scope || !folderName.trim()) return;

    setIsCreating(true);

    try {
      await createAccountFolder({
        label: folderName.trim(),
        scope,
      });

      setNotification({
        title: 'Success',
        message: `Folder "${folderName.trim()}" has been created successfully`,
        type: 'success',
      });

      if (onCreateSuccess) {
        onCreateSuccess();
      }

      // Reset form
      setFolderName('');
      onClose();
    } catch (error) {
      console.error('Error creating folder:', error);
      setNotification({
        title: 'Error',
        message: error?.error_message || 'Failed to create folder',
        type: 'error',
      });
      // Reset form and close even on error
      setFolderName('');
      onClose();
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    setFolderName('');
    onClose();
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      aria-labelledby="create-folder-dialog"
      size="sm"
      hideBackdrop={false}
      className="!h-auto"
    >
      <Dialog.Content>
        <Box className="p-6" onClick={(e) => e.stopPropagation()}>
          <Typography
            data-testid="create-folder-dialog-title"
            className="text-neutral-800 mb-4 text-lg font-semibold"
          >
            Create New Folder
          </Typography>

          <Box className="mb-6">
            <Typography className="text-sm text-neutral-700 mb-2">
              Folder Name
            </Typography>
            <Input
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              placeholder="Enter folder name"
              className="w-full"
              data-testid="create-folder-name-input"
              disabled={isCreating}
            />
          </Box>

          <Box className="flex justify-end gap-3">
            <Button
              variant="outlined"
              color="neutral"
              onClick={handleClose}
              className="px-4"
              data-testid="create-folder-dialog-cancel-button"
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button
              variant="solid"
              color="primary"
              onClick={handleCreate}
              className="px-4"
              data-testid="create-folder-dialog-create-button"
              loading={isCreating}
              disabled={isCreating || !folderName.trim()}
            >
              Create
            </Button>
          </Box>
        </Box>
      </Dialog.Content>
    </Dialog>
  );
};

export default CreateFolderDialog;
